import { useEffect, useMemo, useRef, useState } from "react";
import {
  decodeFragment,
  type DoczFragment,
  fragmentText,
  renderFragmentToHtml,
} from "@norkron/web-docz";
import {
  type CanvasSurfaceHandle,
  DocumentMirror,
  loadPageLayout,
  type PageLayoutModule,
} from "@norkron/web-page-layout";
import type { MentionRef } from "../../api/types";
import { useDevicePixelRatio } from "../../lib/useDevicePixelRatio";
import { useDoczModule } from "../../lib/useDoczModule";
import {
  type CommentSurfaceMount,
  mountCommentSurface,
} from "./commentCanvasSurface";
import { commentMirrorBlocks } from "./commentSurfaceModel";
import {
  COMMENT_SURFACE_OVERSCAN_CSS,
  commentSurfaceBudget,
  type SurfaceBudget,
} from "./commentSurfaceBudget";
import "./commentCanvas.css";

/**
 * `<CommentCanvasBody>` — A COMMENT BODY ON THE DOCUMENT ENGINE.
 *
 * Owner decision **D-E** (`.claude/plans/canvas-unification.md`): *no DOM renders
 * any docz IR anywhere.* A `docz-fragment` comment body is real docz IR — runs
 * and typed marks, lowered through the SAME `html_writer` the document uses — so
 * it is a mini-document, not chatter, and it paints through the SAME `pl_layout`
 * pass that produces the PDF and drives read mode. The owner was offered the
 * option of keeping comment prose on the DOM and explicitly overrode it for
 * absolute one-technology consistency; there is deliberately NO DOM painter
 * fallback for this path.
 *
 * RULE ZERO / HR13. Nothing here re-implements the surface: the mount, the
 * drag-select, double-click word select, ⌘A and the IR-sliced copy are
 * `@norkron/web-page-layout`'s `createCanvasSurface` (`interactivity: 'read'`);
 * the accessible projection is its painter-agnostic `DocumentMirror`, REUSED, not
 * rebuilt; the scale is resolved through elaborate's ONE presentation policy
 * (`canvasPresentation`), never decided here. What is elaborate's is in
 * `commentSurfaceModel` (the byte-range link/mention targets) and the wiring
 * below.
 *
 * ## THE THREE COSTS THE OWNER ACCEPTED, and how each is paid
 *
 * **1. Links and mentions stay clickable.** A canvas has no `<a>`. A link is its
 * byte range (`fragmentStyleMarks`) and a mention chip is the byte range of a
 * literal `@<recorded name>`; both are measured with `surface.rangeRects` into
 * painted bands, tinted so they LOOK interactive, and hit-tested with
 * `surface.pagePointAt` + containment. `pagePointAt` and not `positionAt`: the
 * latter is the caret hit test and it SNAPS, so a click in blank space would open
 * a link nowhere near the pointer. Hover repaints the band and sets
 * `cursor: pointer` — without it there is no way to DISCOVER that the words are
 * interactive.
 *
 * **2. Accessibility.** Every canvas is `aria-hidden="true"` and `DocumentMirror`
 * (`exposure: 'exclusive'`) is the sole representation: `role="document"`, the
 * comment's own semantic HTML, byte-stamped, `translate="no"`, and out of the tab
 * order. HONEST GAP: canvas link/mention ACTIVATION is pointer-only. Assistive
 * technology reaches the link through the mirror's real `<a href>` in browse mode
 * (it is `tabindex="-1"`, programmatically focusable, still announced as a link);
 * a sighted keyboard-only user cannot activate a comment link on the painted
 * surface. That is a real regression against the DOM body and it is reported, not
 * hidden — closing it needs a focus-ring-bearing overlay affordance, which is a
 * `web-page-layout` capability, not an app patch.
 *
 * **3. N canvases per thread.** VIRTUALISED: a card mounts its surface only when
 * it is in (or within {@link COMMENT_SURFACE_OVERSCAN_CSS} of) the viewport, and
 * tears it down when it leaves — so a thread costs the VIEWPORT, not the thread.
 * A process-wide cap (`commentSurfaceBudget`) is the second bound. Measured cost
 * is in `commentCanvasGeometry.test.ts`.
 *
 * ## THE SECURITY BOUNDARY DID NOT MOVE
 *
 * `sanitizeCommentHtml` (web-docz `COMMENT_PROFILE`) was THE XSS boundary on
 * attacker-controllable comment text before `dangerouslySetInnerHTML`. There is no
 * `dangerouslySetInnerHTML` here — but the a11y mirror IS a DOM subtree, and its
 * own profile deliberately admits RELATIVE hrefs (a document needs `#section-3`).
 * So `commentMirrorBlocks` runs `sanitizeCommentHtml` FIRST and the mirror
 * sanitizes over already-sanitized markup: composition is an INTERSECTION, and
 * the narrower comment policy still governs. See `commentSurfaceModel`.
 */

/** Cancel a visibility subscription. */
export type Unobserve = () => void;

/** Subscribe to "is this element on (or near) screen". Injectable so a test can
 *  drive virtualisation deterministically — an `IntersectionObserver` cannot be
 *  stepped by hand. */
export type ObserveVisibility = (
  el: HTMLElement,
  onChange: (visible: boolean) => void,
) => Unobserve;

/**
 * The default: a real `IntersectionObserver` with a one-card overscan.
 *
 * When the platform has none, the honest answer is **visible** — a runtime that
 * cannot report visibility must not be silently left with unpainted comments. The
 * budget is what keeps that case bounded.
 */
export const observeVisibilityDefault: ObserveVisibility = (el, onChange) => {
  const Ctor = (
    globalThis as {
      IntersectionObserver?: typeof IntersectionObserver;
    }
  ).IntersectionObserver;
  if (!Ctor) {
    onChange(true);
    return () => {};
  }
  const io = new Ctor(
    (entries) => {
      for (const e of entries) onChange(e.isIntersecting);
    },
    { rootMargin: `${COMMENT_SURFACE_OVERSCAN_CSS}px 0px` },
  );
  io.observe(el);
  return () => io.disconnect();
};

export interface CommentCanvasBodyProps {
  /** The STORED comment body: a stable-JSON docz fragment. */
  readonly json: string;
  /** The comment's recorded mentions — what a literal `@Name` is matched
   *  against, so a multi-word name resolves. */
  readonly mentions: readonly MentionRef[];
  /** Accessible name for the mirror's `role="document"` landmark. */
  readonly label?: string;
  /** BCP-47 language of the comment (WCAG 3.1.2) — a German note must not be
   *  read out in an English voice. */
  readonly lang?: string;
  /** A mention chip was activated. Absent ⇒ the chip paints but does nothing,
   *  which is the current product behaviour (the DOM chip had no handler
   *  either) — stated rather than implied. */
  readonly onOpenMention?: (userId: number, name: string) => void;
  readonly testId?: string;
  /** DI seam (tests / E2E): resolve the `pdfz-layout.wasm` engine. */
  readonly loadEngine?: () => Promise<PageLayoutModule>;
  /** DI seam (tests): the visibility signal that drives virtualisation. */
  readonly observeVisibility?: ObserveVisibility;
  /** DI seam (tests): the live-surface ceiling. */
  readonly budget?: SurfaceBudget;
  /** DI seam (tests): open a link. Defaults to a noopener new tab. */
  readonly openHref?: (href: string) => void;
  /**
   * OBSERVABILITY seam: the live surface, once it is painting.
   *
   * It exists for the real-browser proof harness (`web/comment-canvas-proof`),
   * which has to drive a REAL pointer at the CENTRE OF A PAINTED BAND. Deriving
   * that coordinate outside the engine would mean re-deriving wrap arithmetic and
   * the points→CSS scale in the test — i.e. a second geometry implementation,
   * which is exactly what would drift and make the proof lie. Handing over the
   * surface lets the harness ask `rangeRects` + `viewportRectFor`, the engine's
   * own answers.
   *
   * Called with `null` on teardown. Nothing in the product wires it.
   */
  readonly onSurfaceReady?: (surface: CanvasSurfaceHandle | null) => void;
}

/** `painted` = the canvas owns the pixels. `fallback` = the mirror is visible
 *  instead (engine failure or an exhausted budget). `pending` = not yet decided
 *  (off screen, or the docz module still loading). */
type SurfaceState = "pending" | "painted" | "fallback";

function openHrefDefault(href: string): void {
  const w = globalThis as { open?: (u: string, t?: string, f?: string) => unknown };
  w.open?.(href, "_blank", "noopener,noreferrer");
}

export function CommentCanvasBody(props: CommentCanvasBodyProps) {
  const {
    json,
    mentions,
    label,
    lang,
    onOpenMention,
    testId = "comment-body",
    loadEngine = loadPageLayout,
    observeVisibility = observeVisibilityDefault,
    budget = commentSurfaceBudget,
    openHref = openHrefDefault,
    onSurfaceReady,
  } = props;

  const dpr = useDevicePixelRatio(2);
  const rootRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<CanvasSurfaceHandle | null>(null);
  const mirrorRef = useRef<DocumentMirror | null>(null);
  const cbRef = useRef({ onOpenMention, openHref, onSurfaceReady });
  cbRef.current = { onOpenMention, openHref, onSurfaceReady };

  const [visible, setVisible] = useState(false);
  const [state, setState] = useState<SurfaceState>("pending");
  const [failure, setFailure] = useState<string | null>(null);

  const module = useDoczModule();

  const fragment = useMemo<DoczFragment | null>(() => {
    try {
      return decodeFragment(json);
    } catch {
      return null;
    }
  }, [json]);

  /** The comment's own semantic HTML, for the ACCESSIBLE PROJECTION only. Never
   *  injected as markup by this component — `commentMirrorBlocks` sanitizes it
   *  under the comment profile and `DocumentMirror` owns the mount. */
  const html = useMemo<string | null>(() => {
    if (!fragment || !module) return null;
    try {
      return renderFragmentToHtml(module, fragment);
    } catch {
      return null;
    }
  }, [fragment, module]);

  const irText = useMemo(
    () => (fragment ? fragmentText(fragment) : ""),
    [fragment],
  );

  // ── VIRTUALISATION: subscribe to on-screen-ness ────────────────────────────
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    return observeVisibility(el, setVisible);
  }, [observeVisibility]);

  // ── the surface + the mirror, as ONE lifecycle ─────────────────────────────
  //
  // They are built in one effect because `createCanvasSurface` calls
  // `container.replaceChildren()`: a mirror mounted by a separate effect would be
  // wiped the next time the surface was rebuilt, silently deleting the comment's
  // only accessible representation.
  useEffect(() => {
    const host = hostRef.current;
    const root = rootRef.current;
    if (!host || !root || !fragment || html == null || module == null) return;

    let disposed = false;
    let mounted: CommentSurfaceMount | null = null;
    const key = `${testId}:${json.length}:${irText.slice(0, 24)}`;
    let slot = false;

    /**
     * The accessible projection.
     *
     * `exposure: 'exclusive'` is LOAD-BEARING. The mirror's own default is
     * `'silent'` (present but `aria-hidden`), which is right when it shadows a
     * surface that is ALREADY accessible HTML, because two exposed copies read the
     * document to a screen reader twice. This surface is a `<canvas>`: it has NO
     * accessibility tree at all, so a silent mirror over it would leave the
     * comment with no representation whatsoever. Here the mirror IS the comment,
     * and it says so.
     *
     * `visible` is the wasm-failure / budget-denied presentation — un-clip it and
     * let the browser reflow it as ordinary text. Not layout-faithful, and never
     * to be sold as such; it is the difference between a readable comment and an
     * empty box.
     */
    const mountMirror = (painted: boolean): void => {
      const mirror = new DocumentMirror(host, {
        ...(label !== undefined ? { label } : {}),
        ...(lang !== undefined ? { lang } : {}),
        exposure: "exclusive",
        ...(painted ? {} : { visible: true }),
        testId: `${testId}-mirror`,
      });
      mirror.update(commentMirrorBlocks(html, irText, lang));
      mirrorRef.current = mirror;
    };

    /** Tear-down for the paths where the mirror is the ONLY thing mounted. */
    const releaseMirror = () => () => {
      mirrorRef.current = null;
      host.replaceChildren();
    };

    if (!visible) {
      // Off screen: no wasm, no layout pass, no backing store — the primary cost
      // bound. The mirror is still mounted (clipped) so a screen reader walking a
      // long thread reads EVERY comment, not only the ones the sighted viewport
      // happens to be painting.
      mountMirror(true);
      setState("pending");
      return releaseMirror();
    }

    slot = budget.admit(key);
    if (!slot) {
      // The ceiling, not a failure: present the comment readably and wait for a
      // scroll to free a slot. `failure` stays null so no cause is invented.
      setFailure(null);
      setState("fallback");
      mountMirror(false);
      return releaseMirror();
    }

    void (async () => {
      try {
        const engine = await loadEngine();
        if (disposed) return;
        mounted = mountCommentSurface({
          host,
          // The theme probe goes on the OUTER node: the surface owns `host`'s
          // children and a repaint is free to replace them.
          themeHost: root,
          docz: module,
          engine,
          fragment,
          mentions,
          blockId: key,
          dpr,
          onActivate: (target) => {
            if (target.kind === "link") cbRef.current.openHref(target.href);
            else cbRef.current.onOpenMention?.(target.userId, target.name);
          },
        });
        surfaceRef.current = mounted.surface;
        // THE MIRROR, after the surface (which replaced the host's children).
        mountMirror(true);
        setFailure(null);
        setState("painted");
        cbRef.current.onSurfaceReady?.(mounted.surface);
      } catch (err) {
        if (disposed) return;
        const message = err instanceof Error ? err.message : String(err);
        // LOUD. A comment that silently stops painting is indistinguishable from
        // an empty comment, and this path is reachable in the field (blocked
        // wasm, an offline asset).
        console.error(
          `[CommentCanvasBody] the layout engine could not paint this comment; ` +
            `presenting the accessible mirror visibly instead: ${message}`,
        );
        mounted?.dispose();
        mounted = null;
        surfaceRef.current = null;
        host.replaceChildren();
        setFailure(message);
        setState("fallback");
        mountMirror(false);
      }
    })();

    return () => {
      disposed = true;
      if (surfaceRef.current) cbRef.current.onSurfaceReady?.(null);
      mounted?.dispose();
      mounted = null;
      surfaceRef.current = null;
      mirrorRef.current = null;
      host.replaceChildren();
      if (slot) budget.release(key);
    };
  }, [
    fragment,
    html,
    irText,
    json,
    module,
    visible,
    dpr,
    label,
    lang,
    mentions,
    testId,
    loadEngine,
    budget,
  ]);

  // Before the docz module resolves, or when the stored body will not decode,
  // present the PLAIN TEXT (XSS-free, and the same degradation the DOM path had).
  // A comment body never throws and is never blank.
  if (!fragment || html == null) {
    return (
      <div
        ref={rootRef}
        className="comment-body ela-comment-canvas"
        data-testid={testId}
        data-surface-state="pending"
      >
        {fragment ? irText : json}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="comment-body ela-comment-canvas"
      data-testid={testId}
      data-surface-state={state}
      data-comment-surface="canvas"
    >
      <div
        ref={hostRef}
        className="ela-comment-canvas__surface"
        // A NAMED tab stop. The engine makes the read surface focusable so ⌘A /
        // ⌘C reach it; a focusable element with no accessible name is a WCAG
        // 4.1.2 failure, and the canvas paints no name of its own.
        role="group"
        aria-label={
          label != null
            ? `${label} — comment text, selectable`
            : "Comment text, selectable"
        }
      />
      {state === "fallback" && failure != null ? (
        <p className="ela-comment-canvas__notice">
          {/* ⛔ NOT `{failure}`. That was the raw exception text from the wasm
              mount — an internal string, shown verbatim to a client. The cause
              still goes to the console (see the `console.error` above), which is
              where an operator can read it. The reader gets the only two facts
              that change what they do: the words are all here, and the styling
              is not. */}
          Shown as plain text — this comment couldn&apos;t be laid out.
        </p>
      ) : null}
    </div>
  );
}
